import React, { useState, useEffect } from 'react';
import {
  Dialog, DialogTitle, DialogContent, DialogActions, Box, Typography,
  Button, IconButton, TextField, Alert, InputAdornment, Chip, LinearProgress
} from '@mui/material';
import {
  Close, CheckCircle, MonetizationOn, CreditCard, QrCode2,
  CardGiftcard, PointOfSale
} from '@mui/icons-material';
import { motion, AnimatePresence } from 'framer-motion';
import { useThemeModeContext } from '../hooks/useThemeMode';
import apiService from '../services/api';

type PayMode = 'Cash' | 'Card' | 'UPI' | 'Complimentary';

interface PaymentModalProps {
  open: boolean;
  onClose: () => void;
  billId: number;
  grandTotal: number;
  onPaymentSuccess: () => void;
}

const MODES: { key: PayMode; label: string; icon: React.ReactNode; color: string; activeGrad: string }[] = [
  { key: 'Cash',          label: 'Cash',   icon: <MonetizationOn />, color: '#10b981', activeGrad: 'linear-gradient(135deg,#10b981 0%,#059669 100%)' },
  { key: 'Card',          label: 'Card',   icon: <CreditCard />,     color: '#6366f1', activeGrad: 'linear-gradient(135deg,#6366f1 0%,#8b5cf6 100%)' },
  { key: 'UPI',           label: 'UPI',    icon: <QrCode2 />,        color: '#3b82f6', activeGrad: 'linear-gradient(135deg,#3b82f6 0%,#2563eb 100%)' },
  { key: 'Complimentary', label: 'Comp.',  icon: <CardGiftcard />,   color: '#f59e0b', activeGrad: 'linear-gradient(135deg,#f59e0b 0%,#d97706 100%)' },
];

// Next denomination strictly above the given amount
function nextDenomination(amount: number): number {
  if (amount <= 0)    return 0;
  if (amount < 10)    return 10;
  if (amount < 20)    return 20;
  if (amount < 50)    return 50;
  if (amount < 100)   return 100;
  if (amount < 200)   return 200;
  if (amount < 500)   return 500;
  if (amount < 1000)  return 1000;
  if (amount < 2000)  return 2000;
  if (amount < 5000)  return 5000;
  return Math.ceil(amount / 1000) * 1000;
}

export const PaymentModal: React.FC<PaymentModalProps> = ({
  open, onClose, billId, grandTotal, onPaymentSuccess,
}) => {
  const { isDark } = useThemeModeContext();

  const [activeTab,    setActiveTab]    = useState<PayMode>('Cash');
  // Manual inputs for non-cash modes only
  const [cardAmt,      setCardAmt]      = useState('');
  const [upiAmt,       setUpiAmt]       = useState('');
  const [compAmt,      setCompAmt]      = useState('');
  // Card / UPI refs
  const [cardRef,      setCardRef]      = useState('');
  const [upiRef,       setUpiRef]       = useState('');
  // Cash: user enters what customer gives
  const [cashTendered, setCashTendered] = useState('');

  const [error,   setError]   = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  // ── Derived numbers ────────────────────────────────────────────────────────
  const cardVal  = parseFloat(cardAmt)  || 0;
  const upiVal   = parseFloat(upiAmt)   || 0;
  const compVal  = parseFloat(compAmt)  || 0;
  const otherPaid = cardVal + upiVal + compVal;

  // How much of the bill still needs to be covered by cash
  const cashNeeded = Math.max(0, Math.round((grandTotal - otherPaid) * 100) / 100);

  const tenderedVal = parseFloat(cashTendered) || 0;
  // Cash applied = what actually goes to the bill (never more than what's owed)
  const cashApplied = Math.min(tenderedVal, cashNeeded);
  // Change the cashier returns to customer
  const returnAmt   = Math.max(0, Math.round((tenderedVal - cashNeeded) * 100) / 100);

  const totalPaid   = Math.round((cashApplied + otherPaid) * 100) / 100;
  const remaining   = Math.round((grandTotal - totalPaid) * 100) / 100;
  const isPaid      = remaining <= 0.01;
  const progressPct = Math.min(100, (totalPaid / grandTotal) * 100);

  // ── Reset on open ──────────────────────────────────────────────────────────
  useEffect(() => {
    if (open) {
      setActiveTab('Cash');
      setCardAmt(''); setUpiAmt(''); setCompAmt('');
      setCardRef(''); setUpiRef('');
      setCashTendered('');
      setError(null);
    }
  }, [open, grandTotal]);

  // ── Auto-fill cash tendered when cashNeeded changes ────────────────────────
  useEffect(() => {
    if (cashNeeded <= 0) { setCashTendered(''); return; }
    // Only auto-fill if the user hasn't manually entered a tendered amount yet
    // OR if the current tendered value is less than cashNeeded (invalid)
    if (tenderedVal < cashNeeded || cashTendered === '') {
      setCashTendered(nextDenomination(cashNeeded).toString());
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [cashNeeded]);

  // ── Confirm ────────────────────────────────────────────────────────────────
  const handleConfirm = async () => {
    if (!isPaid) {
      setError(`Need ₹${remaining.toFixed(2)} more to complete payment.`);
      return;
    }
    const payments: { paymentMode: string; amount: number; transactionRef?: string | null }[] = [];
    if (cashApplied > 0) payments.push({ paymentMode: 'Cash',          amount: cashApplied });
    if (cardVal    > 0)  payments.push({ paymentMode: 'Card',          amount: cardVal,  transactionRef: cardRef || null });
    if (upiVal     > 0)  payments.push({ paymentMode: 'UPI',           amount: upiVal,   transactionRef: upiRef  || null });
    if (compVal    > 0)  payments.push({ paymentMode: 'Complimentary', amount: compVal });
    if (payments.length === 0) { setError('Enter at least one payment.'); return; }

    try {
      setLoading(true);
      setError(null);
      await apiService.processBillPayment(billId, { payments });
      onPaymentSuccess();
    } catch (err: any) {
      setError(err?.response?.data?.message || err.message || 'Payment failed');
    } finally {
      setLoading(false);
    }
  };

  // ── Helpers ─────────────────────────────────────────────────────────────
  const fillRemainingForTab = () => {
    if (activeTab === 'Cash') {
      // Fill tendered with next denomination above cashNeeded
      setCashTendered(nextDenomination(cashNeeded).toString());
    } else {
      const cur = parseFloat(
        activeTab === 'Card' ? cardAmt : activeTab === 'UPI' ? upiAmt : compAmt
      ) || 0;
      const fill = (cur + remaining).toFixed(2);
      if (activeTab === 'Card') setCardAmt(fill);
      else if (activeTab === 'UPI') setUpiAmt(fill);
      else setCompAmt(fill);
    }
  };

  const clearAll = () => {
    setCardAmt(''); setUpiAmt(''); setCompAmt('');
    setCardRef(''); setUpiRef('');
    setCashTendered('');
  };

  // For tab badges — show how much each mode contributes to the bill
  const tabAmount = (key: PayMode): number => {
    if (key === 'Cash') return cashApplied;
    if (key === 'Card') return cardVal;
    if (key === 'UPI')  return upiVal;
    return compVal;
  };

  const activeMeta = MODES.find(m => m.key === activeTab)!;
  const panelBg    = isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)';
  const panelBorder = isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)';

  const tabBg = (key: PayMode) => {
    const has = tabAmount(key) > 0;
    const isActive = activeTab === key;
    const meta = MODES.find(m => m.key === key)!;
    if (isActive) return meta.activeGrad;
    if (has) return isDark ? `${meta.color}22` : `${meta.color}15`;
    return isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)';
  };

  const tabBorder = (key: PayMode) => {
    const has = tabAmount(key) > 0;
    const isActive = activeTab === key;
    const meta = MODES.find(m => m.key === key)!;
    if (isActive || has) return `1.5px solid ${meta.color}`;
    return `1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.1)'}`;
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="xs" fullWidth>
      <DialogTitle sx={{ p: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box display="flex" alignItems="center" gap={1}>
          <PointOfSale color="primary" fontSize="small" />
          <Typography variant="h6" fontWeight={700}>Process Payment</Typography>
        </Box>
        <IconButton onClick={onClose} size="small"><Close fontSize="small" /></IconButton>
      </DialogTitle>

      <DialogContent sx={{ px: 2.5, pb: 1, pt: 1 }}>
        {/* Bill total + progress */}
        <Box sx={{ mb: 2.5 }}>
          <Box display="flex" justifyContent="space-between" alignItems="baseline" mb={0.5}>
            <Typography variant="caption" color="text.secondary" fontWeight={600} sx={{ textTransform: 'uppercase', letterSpacing: 0.5 }}>
              Bill Total
            </Typography>
            <Typography variant="h4" fontWeight={900} color="primary.main" lineHeight={1}>
              ₹{grandTotal.toFixed(2)}
            </Typography>
          </Box>
          <LinearProgress
            variant="determinate"
            value={progressPct}
            sx={{
              height: 6, borderRadius: 4, mb: 0.5,
              bgcolor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.08)',
              '& .MuiLinearProgress-bar': {
                background: isPaid ? 'linear-gradient(90deg,#10b981,#059669)' : `linear-gradient(90deg,${activeMeta.color},${activeMeta.color}aa)`,
                borderRadius: 4, transition: 'all 0.3s ease',
              },
            }}
          />
          <Box display="flex" justifyContent="space-between">
            <Typography variant="caption" color="text.secondary">
              Paid: <strong>₹{totalPaid.toFixed(2)}</strong>
            </Typography>
            <Typography variant="caption" fontWeight={700} sx={{ color: isPaid ? '#10b981' : '#ef4444' }}>
              {isPaid ? '✓ Fully Settled' : `Due: ₹${remaining.toFixed(2)}`}
            </Typography>
          </Box>
        </Box>

        {error && (
          <Alert severity="error" sx={{ mb: 2, py: 0 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Mode tabs */}
        <Box display="flex" gap={1} sx={{ mb: 2 }}>
          {MODES.map(mode => {
            const amt = tabAmount(mode.key);
            const isActive = activeTab === mode.key;
            return (
              <Box
                key={mode.key}
                onClick={() => setActiveTab(mode.key)}
                sx={{
                  flex: 1, py: 1.2, px: 0.5, borderRadius: '14px', cursor: 'pointer',
                  background: tabBg(mode.key), border: tabBorder(mode.key),
                  display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.4,
                  transition: 'all 0.18s ease',
                  transform: isActive ? 'translateY(-2px)' : 'none',
                  boxShadow: isActive ? `0 4px 12px ${mode.color}40` : 'none',
                }}
              >
                <Box sx={{ color: isActive ? '#fff' : mode.color, display: 'flex', fontSize: '1.2rem' }}>
                  {mode.icon}
                </Box>
                <Typography variant="caption" fontWeight={700} sx={{ color: isActive ? '#fff' : 'text.primary', fontSize: '0.7rem' }}>
                  {mode.label}
                </Typography>
                {amt > 0 && (
                  <Typography variant="caption" sx={{ color: isActive ? 'rgba(255,255,255,0.9)' : mode.color, fontSize: '0.65rem', fontWeight: 800, lineHeight: 1 }}>
                    ₹{amt.toFixed(0)}
                  </Typography>
                )}
              </Box>
            );
          })}
        </Box>

        {/* Active tab panel */}
        <AnimatePresence mode="wait">
          <motion.div key={activeTab} initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -6 }} transition={{ duration: 0.15 }}>
            <Box sx={{ p: 2, borderRadius: '16px', background: panelBg, border: `1px solid ${panelBorder}` }}>

              {/* Panel header */}
              <Box display="flex" alignItems="center" justifyContent="space-between" mb={1.5}>
                <Box display="flex" alignItems="center" gap={1}>
                  <Box sx={{ color: activeMeta.color }}>{activeMeta.icon}</Box>
                  <Typography variant="subtitle2" fontWeight={700}>{activeMeta.label} Payment</Typography>
                </Box>
                <Box display="flex" gap={0.75}>
                  <Button size="small" variant="outlined" onClick={fillRemainingForTab}
                    disabled={activeTab === 'Cash' ? cashNeeded <= 0 : remaining <= 0}
                    sx={{ borderRadius: '10px', fontSize: '0.68rem', py: 0.3, px: 1, minWidth: 0 }}>
                    +Due
                  </Button>
                  <Button size="small" variant="outlined" color="error" onClick={clearAll}
                    sx={{ borderRadius: '10px', fontSize: '0.68rem', py: 0.3, px: 1, minWidth: 0 }}>
                    Clear
                  </Button>
                </Box>
              </Box>

              {/* ── CASH TAB ── */}
              {activeTab === 'Cash' && (
                <Box>
                  {/* Two columns: Cash Tendered | Return Amount */}
                  <Box display="flex" gap={1.5} alignItems="flex-start">
                    {/* Left: Cash Tendered input */}
                    <Box flex={1}>
                      <Typography variant="caption" color="text.secondary" fontWeight={700}
                        sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
                        Cash Tendered
                      </Typography>
                      <TextField
                        size="small" fullWidth type="number" autoFocus
                        value={cashTendered}
                        onChange={e => { setError(null); setCashTendered(e.target.value); }}
                        placeholder={`e.g. ₹${nextDenomination(cashNeeded)}`}
                        slotProps={{
                          input: {
                            startAdornment: <InputAdornment position="start">
                              <Typography variant="body2" fontWeight={700} color="text.secondary">₹</Typography>
                            </InputAdornment>,
                          }
                        }}
                      />
                      {/* Quick denomination buttons */}
                      <Box display="flex" gap={0.5} mt={0.75} flexWrap="wrap">
                        {[
                          nextDenomination(cashNeeded),
                          nextDenomination(nextDenomination(cashNeeded)),
                          Math.ceil(cashNeeded / 10) * 10 === cashNeeded ? cashNeeded : Math.ceil(cashNeeded / 10) * 10,
                        ].filter((v, i, arr) => arr.indexOf(v) === i && v > 0).slice(0, 3).map(denom => (
                          <Button key={denom} size="small" variant="outlined"
                            onClick={() => setCashTendered(denom.toString())}
                            sx={{
                              borderRadius: '8px', py: 0.2, px: 1, fontSize: '0.7rem', fontWeight: 700, minWidth: 0,
                              borderColor: cashTendered === denom.toString() ? '#10b981' : undefined,
                              color: cashTendered === denom.toString() ? '#10b981' : undefined,
                            }}>
                            ₹{denom}
                          </Button>
                        ))}
                      </Box>
                    </Box>

                    {/* Right: Return Amount display */}
                    <Box flex={1}>
                      <Typography variant="caption" fontWeight={700}
                        sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem', color: returnAmt > 0 ? '#10b981' : 'text.secondary' }}>
                        Return to Customer
                      </Typography>
                      <Box
                        sx={{
                          height: 40, borderRadius: '10px',
                          display: 'flex', alignItems: 'center', justifyContent: 'center',
                          background: returnAmt > 0
                            ? (isDark ? 'rgba(16,185,129,0.15)' : 'rgba(16,185,129,0.1)')
                            : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)'),
                          border: `1.5px solid ${returnAmt > 0 ? 'rgba(16,185,129,0.5)' : (isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)')}`,
                          transition: 'all 0.2s ease',
                        }}
                      >
                        <Typography variant="h6" fontWeight={900} lineHeight={1}
                          sx={{ color: returnAmt > 0 ? '#10b981' : 'text.disabled' }}>
                          {cashTendered === '' ? '—' : returnAmt > 0 ? `₹${returnAmt.toFixed(2)}` : '₹0.00'}
                        </Typography>
                      </Box>

                      {/* Applied to bill label */}
                      {cashApplied > 0 && (
                        <Typography variant="caption" color="text.secondary"
                          sx={{ display: 'block', textAlign: 'center', mt: 0.5, fontSize: '0.65rem' }}>
                          Applied: ₹{cashApplied.toFixed(2)}
                        </Typography>
                      )}
                    </Box>
                  </Box>

                  {/* Warning if tendered < cashNeeded */}
                  {cashTendered !== '' && tenderedVal < cashNeeded && (
                    <Alert severity="warning" sx={{ mt: 1, py: 0, fontSize: '0.75rem' }}>
                      Tendered ₹{tenderedVal.toFixed(2)} is less than due ₹{cashNeeded.toFixed(2)}.
                      Short by ₹{(cashNeeded - tenderedVal).toFixed(2)}.
                    </Alert>
                  )}
                </Box>
              )}

              {/* ── CARD TAB ── */}
              {activeTab === 'Card' && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <TextField size="small" fullWidth type="number" autoFocus label="Card Amount"
                    value={cardAmt} onChange={e => { setError(null); setCardAmt(e.target.value); }}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><Typography variant="body2" fontWeight={700} color="text.secondary">₹</Typography></InputAdornment> } }}
                  />
                  {cardVal > 0 && (
                    <TextField size="small" fullWidth label="Approval Code / Last 4 Digits (optional)"
                      value={cardRef} onChange={e => setCardRef(e.target.value)} />
                  )}
                </Box>
              )}

              {/* ── UPI TAB ── */}
              {activeTab === 'UPI' && (
                <Box display="flex" flexDirection="column" gap={1.5}>
                  <TextField size="small" fullWidth type="number" autoFocus label="UPI Amount"
                    value={upiAmt} onChange={e => { setError(null); setUpiAmt(e.target.value); }}
                    slotProps={{ input: { startAdornment: <InputAdornment position="start"><Typography variant="body2" fontWeight={700} color="text.secondary">₹</Typography></InputAdornment> } }}
                  />
                  {upiVal > 0 && (
                    <TextField size="small" fullWidth label="UPI Transaction ID / UPI ID (optional)"
                      value={upiRef} onChange={e => setUpiRef(e.target.value)} />
                  )}
                </Box>
              )}

              {/* ── COMPLIMENTARY TAB ── */}
              {activeTab === 'Complimentary' && (
                <TextField size="small" fullWidth type="number" autoFocus label="Complimentary Amount"
                  value={compAmt} onChange={e => { setError(null); setCompAmt(e.target.value); }}
                  slotProps={{ input: { startAdornment: <InputAdornment position="start"><Typography variant="body2" fontWeight={700} color="text.secondary">₹</Typography></InputAdornment> } }}
                />
              )}
            </Box>
          </motion.div>
        </AnimatePresence>

        {/* Split summary */}
        {[
          { key: 'Cash' as PayMode, label: 'Cash', amt: cashApplied, color: '#10b981' },
          { key: 'Card' as PayMode, label: 'Card', amt: cardVal,    color: '#6366f1' },
          { key: 'UPI'  as PayMode, label: 'UPI',  amt: upiVal,     color: '#3b82f6' },
          { key: 'Complimentary' as PayMode, label: 'Comp', amt: compVal, color: '#f59e0b' },
        ].filter(m => m.amt > 0).length > 1 && (
          <Box sx={{ mt: 1.5, px: 1.5, py: 1, borderRadius: '10px', background: panelBg, border: `1px solid ${panelBorder}` }}>
            <Typography variant="caption" color="text.secondary" fontWeight={700}
              sx={{ display: 'block', mb: 0.5, textTransform: 'uppercase', letterSpacing: 0.4, fontSize: '0.62rem' }}>
              Split Summary
            </Typography>
            <Box display="flex" flexWrap="wrap" gap={0.75}>
              {[
                { key: 'Cash' as PayMode, label: 'Cash', amt: cashApplied, color: '#10b981' },
                { key: 'Card' as PayMode, label: 'Card', amt: cardVal,    color: '#6366f1' },
                { key: 'UPI'  as PayMode, label: 'UPI',  amt: upiVal,     color: '#3b82f6' },
                { key: 'Complimentary' as PayMode, label: 'Comp', amt: compVal, color: '#f59e0b' },
              ].filter(m => m.amt > 0).map(m => (
                <Chip key={m.key} size="small"
                  label={`${m.label}: ₹${m.amt.toFixed(2)}`}
                  sx={{ fontSize: '0.72rem', fontWeight: 700, bgcolor: isDark ? `${m.color}22` : `${m.color}18`, color: m.color, border: `1px solid ${m.color}55` }}
                />
              ))}
            </Box>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{ px: 2.5, pb: 2.5, pt: 1.5, gap: 1 }}>
        <Button onClick={onClose} variant="outlined" color="inherit" sx={{ borderRadius: '24px', flex: 1 }}>
          Cancel
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          disabled={loading || !isPaid}
          startIcon={isPaid ? <CheckCircle /> : undefined}
          sx={{
            borderRadius: '24px', flex: 2, fontWeight: 700,
            background: isPaid ? 'linear-gradient(90deg,#10b981 0%,#059669 100%)' : undefined,
            boxShadow: isPaid ? '0 4px 12px rgba(16,185,129,0.35)' : 'none',
            transition: 'all 0.2s ease',
          }}
        >
          {loading ? 'Processing…' : isPaid ? `Confirm  ₹${grandTotal.toFixed(2)}` : `Due ₹${remaining.toFixed(2)}`}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
